import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useProducts } from "@/context/ProductContext";
import { ProductCard } from "@/components/catalog/ProductCard";
import { Badge } from "@/components/ui/badge";
import { Heart, Search } from "lucide-react";
import { BottomNav } from "@/components/catalog/BottomNav";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/")({
  component: Index,
});

function Index() {
  const { products, categories, isLoading, getSetting } = useProducts();
  const [banners, setBanners] = useState<any[]>([]);
  const [bannerRatio, setBannerRatio] = useState("16/9");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fetchBanners = async () => {
      const { data } = await supabase.from('banners').select('*').eq('active', true);
      setBanners(data || []);
    };

    const fetchSettings = async () => {
      const ratio = await getSetting('banner_aspect_ratio');
      if (ratio) setBannerRatio(ratio);
    };

    fetchBanners();
    fetchSettings();
  }, []);
 
   useEffect(() => {
     const handleScroll = () => setScrolled(window.scrollY > 20);
     window.addEventListener("scroll", handleScroll);
     return () => window.removeEventListener("scroll", handleScroll);
   }, []);

   const filteredProducts = useMemo(() => {
     let filtered = selectedCategory === "Todos"
       ? products
       : products.filter(p => p.category === selectedCategory);
 
     if (searchQuery) {
       filtered = filtered.filter(p => 
         p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
         p.description.toLowerCase().includes(searchQuery.toLowerCase())
       );
     }
     return filtered;
   }, [products, selectedCategory, searchQuery]);
 
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Carregando catálogo...</p>
        </div>
      </div>
    );
  }

  return (
     <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-0">
       <header className={`sticky top-0 z-40 w-full transition-all duration-300 ${
         scrolled ? "bg-white/90 backdrop-blur-md py-3 border-b shadow-sm" : "bg-transparent py-6"
       }`}>
         <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
           <div className="flex items-center gap-8">
             <div>
               <h1 className="text-xl md:text-2xl font-serif font-bold text-gray-900 tracking-tight">Ateliê da Gaby</h1>
               {!scrolled && <p className="text-[11px] text-gray-400 uppercase tracking-[0.2em] font-medium hidden md:block">Handmade with love</p>}
             </div>

             {/* Desktop Navigation */}
             <nav className="hidden md:flex items-center gap-6 ml-4">
               {categories.map((category) => (
                 <button
                   key={category}
                   onClick={() => setSelectedCategory(category)}
                   className={`text-sm font-medium transition-colors hover:text-pink-500 ${
                     selectedCategory === category ? "text-pink-500" : "text-gray-500"
                   }`}
                 >
                   {category}
                 </button>
               ))}
             </nav>
           </div>

           <div className="flex items-center gap-4">
             <div className="hidden md:flex relative w-64">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
               <Input 
                 placeholder="Buscar no catálogo..." 
                 className="h-10 w-full rounded-full border-gray-100 bg-white/50 pl-9 focus-visible:ring-pink-200"
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
               />
             </div>
             
             <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm border border-gray-100">
               <Heart className="h-5 w-5 text-pink-400 fill-pink-400" />
             </div>
           </div>
         </div>
      </header>

      <main className="mx-auto max-w-7xl px-6">
        {/* Banners */}
        {banners.length > 0 && (
          <div className="mt-4 -mx-2 flex md:mx-0 gap-4 overflow-x-auto pb-4 px-2 md:px-0 scrollbar-hide">
            {banners.map((banner) => (
              <div 
                key={banner.id} 
                className="relative min-w-full md:min-w-[48%] lg:min-w-[32%] rounded-[32px] overflow-hidden shadow-sm"
                style={{ aspectRatio: bannerRatio }}
              >
                <img src={banner.image_url} alt={banner.title || ""} className="w-full h-full object-cover" />
                {banner.title && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6">
                    <h3 className="text-white font-bold text-lg">{banner.title}</h3>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

         <div className="mt-4 mb-8 md:hidden">
           <div className="relative">
             <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
             <Input 
               placeholder="O que você procura hoje?" 
               className="h-12 w-full rounded-2xl border-none bg-white pl-11 shadow-sm focus-visible:ring-pink-200"
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
             />
           </div>
         </div>
 
        {/* Categories (Mobile only) */}
         <div className="mb-8 flex md:hidden gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-2 px-2">
          {categories.map((category) => (
            <Badge
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
               className={`cursor-pointer whitespace-nowrap rounded-xl px-5 py-2 text-sm transition-all border-none shadow-sm ${
                selectedCategory === category 
                   ? "bg-pink-500 text-white hover:bg-pink-600" 
                   : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Badge>
          ))}
        </div>

        {/* Product Grid */}
         <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Footer info */}
         <footer className="mt-16 text-center text-xs text-muted-foreground pb-8">
          <p>© {new Date().getFullYear()} Ateliê da Gaby</p>
        </footer>
      </main>
 
       <BottomNav />
    </div>
  );
}
